import React, { useRef } from 'react';
import Clarity from '@microsoft/clarity';
import { Route, Routes, useSearchParams, useLocation } from 'react-router-dom';
import analyticsTracking from 'src/services/analytics-service';
import useGlobalAnalytics from 'src/global/hooks/useGlobalAnalytics';
import AppRoutes from 'src/routes/AppRoutes';
import VideoHelp from 'src/global/components/video-help/VideoHelp';
import { SidebarProvider, SidebarInset } from 'src/shared/components/ui/sidebar';
import { AppSidebar } from 'src/global/components/sidebar/AppSidebar';
import useAuthService from 'src/shared/hooks/useAuthService';
import { useAnnouncementsContext, AnnouncementsProvider } from 'src/global/contexts/AnnouncementsContext';
import AnnouncementsModal from 'src/global/components/announcements-modal/AnnouncementsModal';
import config from 'src/config';
import styles from './App.module.scss';
import './MUItheme.css';
import Oauth from './login/views/oauth/Oauth';

const { msClarityAnalyticsKey } = config;

const AppContent = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [searchParams] = useSearchParams();
    const { pathname } = useLocation();
    const { isAuthenticated } = useAuthService();

    const shouldHideSiteMenu = searchParams.get('printLayout') === 'true';
    const isLoginPages = pathname.includes('login') || pathname === '/';
    const shouldShowSidebar = !shouldHideSiteMenu && !isLoginPages && isAuthenticated;

    analyticsTracking.initialize();
    useGlobalAnalytics(ref);
    if (msClarityAnalyticsKey) {
        Clarity.init(msClarityAnalyticsKey);
    }

    if (shouldShowSidebar) {
        return (
            <>
                <SidebarProvider>
                    <AppSidebar />
                    <SidebarInset>
                        <div ref={ref} className={styles.app}>
                            <div className={styles.appContent}>
                                <Routes>
                                    <Route path='/oauth' element={<Oauth />} />
                                    <Route path='*' element={<AppRoutes />} />
                                </Routes>
                            </div>
                        </div>
                    </SidebarInset>
                </SidebarProvider>
                <VideoHelp />
                <AnnouncementsModalWrapper />
            </>
        );
    }

    // Original layout for login pages and when not authenticated
    return (
        <>
            <div ref={ref} className={styles.app}>
                <div className={styles.appContent}>
                    <Routes>
                        <Route path='/oauth' element={<Oauth />} />
                        <Route path='*' element={<AppRoutes />} />
                    </Routes>
                </div>
            </div>
            <VideoHelp />
        </>
    );
};

// Separate component to use the announcements context
const AnnouncementsModalWrapper = () => {
    const { announcements, isModalOpen, closeModal } = useAnnouncementsContext();

    return <AnnouncementsModal announcements={announcements} isOpen={isModalOpen} onClose={closeModal} />;
};

const App = () => {
    return (
        <AnnouncementsProvider>
            <AppContent />
        </AnnouncementsProvider>
    );
};

export default App;
